import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { faCalendarDays } from '@fortawesome/free-solid-svg-icons';
import { Backdrop, CloseRow, DayButton, Grid, InlineInput, MonthButton, OpenButton, Popover, PopoverFooter, PopoverHeader, Trigger, TriggerIcon, Weekdays, Wrapper } from './SharedDatePicker.styled';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const WEEKDAY_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const monthYearFormatter = new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' });

const getTodayDateKey = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toDateKey = (value) => {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return getTodayDateKey();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const fromDateKey = (dateKey) => {
  if (!dateKey || typeof dateKey !== 'string') return new Date();
  const [year, month, day] = dateKey.split('-').map(Number);
  if (!year || !month || !day) return new Date();
  return new Date(year, month - 1, day);
};

const formatDateShort = (value) => {
  const date = value instanceof Date ? value : fromDateKey(value);
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};

const formatDateForField = (value) => {
  const date = value instanceof Date ? value : fromDateKey(value);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const normalizeDateFieldDraft = (rawValue) => {
  const digits = rawValue.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const parseDateFieldValue = (rawValue) => {
  const digits = rawValue.replace(/\D/g, '');
  if (digits.length !== 8) return null;
  const day = Number(digits.slice(0, 2));
  const month = Number(digits.slice(2, 4));
  const year = Number(digits.slice(4, 8));
  if (!day || !month || !year) return null;
  const parsed = new Date(year, month - 1, day);
  if (
    parsed.getFullYear() !== year
    || parsed.getMonth() !== month - 1
    || parsed.getDate() !== day
  ) return null;
  return toDateKey(parsed);
};

const formatMonthYear = (value) => {
  const date = value instanceof Date ? value : fromDateKey(value);
  const label = monthYearFormatter.format(date);
  return label.charAt(0).toUpperCase() + label.slice(1);
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SharedDatePicker = ({
  className,
  inputId,
  value,
  onChange,
  ariaLabel = 'Saisir une date',
  dialogLabel = 'Sélecteur de date',
  placeholder = 'JJ/MM/AAAA',
  minDateKey,
  maxDateKey,
  disabled = false,
  allowEmpty = false,
  showQuickActions = true,
}) => {
  const selectedDateKey = useMemo(() => {
    if (allowEmpty && !value) return '';
    return toDateKey(value || new Date());
  }, [allowEmpty, value]);

  const fallbackDateKey = useMemo(() => toDateKey(new Date()), []);
  const selectedOrFallbackDateKey = selectedDateKey || fallbackDateKey;

  const [inputValue, setInputValue] = useState(() => (
    selectedDateKey ? formatDateForField(selectedDateKey) : ''
  ));
  const [isOpen, setIsOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState(null);
  const [calendarViewDate, setCalendarViewDate] = useState(() => {
    const current = fromDateKey(selectedOrFallbackDateKey);
    return new Date(current.getFullYear(), current.getMonth(), 1);
  });

  const wrapperRef = useRef(null);
  const popoverRef = useRef(null);

  const applyDateChange = useCallback((nextDateKey) => {
    if (!nextDateKey || disabled) return;
    let normalizedKey = nextDateKey;
    if (minDateKey && normalizedKey < minDateKey) normalizedKey = minDateKey;
    if (maxDateKey && normalizedKey > maxDateKey) normalizedKey = maxDateKey;
    onChange?.(normalizedKey);
  }, [disabled, maxDateKey, minDateKey, onChange]);

  useEffect(() => {
    setInputValue(selectedDateKey ? formatDateForField(selectedDateKey) : '');
  }, [selectedDateKey]);

  const openDatePicker = useCallback(() => {
    const current = fromDateKey(selectedOrFallbackDateKey);
    setCalendarViewDate(new Date(current.getFullYear(), current.getMonth(), 1));
    setIsOpen(true);
  }, [selectedOrFallbackDateKey]);

  const closeDatePicker = useCallback(() => {
    setIsOpen(false);
    setPopoverStyle(null);
  }, []);

  const toggleDatePicker = useCallback(() => {
    if (isOpen) { closeDatePicker(); return; }
    if (disabled) return;
    openDatePicker();
  }, [closeDatePicker, disabled, isOpen, openDatePicker]);

  const handleInlineDateInputChange = useCallback((event) => {
    const normalizedDraft = normalizeDateFieldDraft(event.target.value);
    setInputValue(normalizedDraft);
    if (allowEmpty && normalizedDraft === '') { onChange?.(''); return; }
    const parsedDateKey = parseDateFieldValue(normalizedDraft);
    if (!parsedDateKey) return;
    applyDateChange(parsedDateKey);
    if (isOpen) { setIsOpen(false); setPopoverStyle(null); }
  }, [allowEmpty, applyDateChange, isOpen, onChange]);

  const handleInlineDateInputBlur = useCallback(() => {
    if (allowEmpty && inputValue.trim() === '') {
      setInputValue('');
      onChange?.('');
      return;
    }
    const parsedDateKey = parseDateFieldValue(inputValue);
    if (!parsedDateKey) {
      setInputValue(selectedDateKey ? formatDateForField(selectedDateKey) : '');
      return;
    }
    const normalizedKey = (() => {
      if (minDateKey && parsedDateKey < minDateKey) return minDateKey;
      if (maxDateKey && parsedDateKey > maxDateKey) return maxDateKey;
      return parsedDateKey;
    })();
    setInputValue(formatDateForField(normalizedKey));
    applyDateChange(normalizedKey);
  }, [allowEmpty, applyDateChange, inputValue, maxDateKey, minDateKey, onChange, selectedDateKey]);

  const shiftCalendarMonth = useCallback((offset) => {
    setCalendarViewDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + offset, 1));
  }, []);

  const selectDateFromCalendar = useCallback((date) => {
    applyDateChange(toDateKey(date));
    closeDatePicker();
  }, [applyDateChange, closeDatePicker]);

  const goToday = useCallback(() => {
    applyDateChange(getTodayDateKey());
    closeDatePicker();
  }, [applyDateChange, closeDatePicker]);

  const goRelativeDay = useCallback((offset) => {
    const nextDate = new Date();
    nextDate.setHours(0, 0, 0, 0);
    nextDate.setDate(nextDate.getDate() + offset);
    applyDateChange(toDateKey(nextDate));
    closeDatePicker();
  }, [applyDateChange, closeDatePicker]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event) => {
      if (wrapperRef.current?.contains(event.target)) return;
      if (popoverRef.current?.contains(event.target)) return;
      setIsOpen(false);
    };
    const handleEscape = (event) => { if (event.key === 'Escape') setIsOpen(false); };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    window.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const updatePopoverPosition = useCallback(() => {
    if (!isOpen || typeof window === 'undefined') return;

    const triggerElement = wrapperRef.current?.querySelector('.date-picker-trigger-inner');
    const popoverElement = popoverRef.current;
    if (!triggerElement || !popoverElement) return;

    const visualViewport = window.visualViewport;
    const viewportWidth = visualViewport?.width || window.innerWidth;
    const viewportHeight = visualViewport?.height || window.innerHeight;
    const viewportOffsetTop = visualViewport?.offsetTop || 0;
    const triggerRect = triggerElement.getBoundingClientRect();
    const popoverRect = popoverElement.getBoundingClientRect();
    const spacing = 8;

    if (viewportWidth <= 768) {
      const mobileWidth = Math.min(340, Math.max(280, viewportWidth - spacing * 2));
      const centeredTop = viewportOffsetTop + (viewportHeight - popoverRect.height) / 2;
      const minTop = viewportOffsetTop + spacing;
      const maxTop = viewportOffsetTop + viewportHeight - popoverRect.height - spacing;
      const top = Math.max(minTop, Math.min(centeredTop, maxTop));
      setPopoverStyle({
        top: `${Math.round(top)}px`,
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        width: `${Math.round(mobileWidth)}px`,
        maxWidth: `${Math.round(mobileWidth)}px`,
        transform: 'translateX(-50%)',
      });
      return;
    }

    let left = triggerRect.left + triggerRect.width / 2 - popoverRect.width / 2;
    left = Math.max(spacing, Math.min(left, viewportWidth - popoverRect.width - spacing));

    const spaceBelow = viewportHeight - triggerRect.bottom - spacing;
    const spaceAbove = triggerRect.top - spacing;
    let top = triggerRect.bottom + spacing;
    if (spaceBelow < popoverRect.height && spaceAbove > spaceBelow) {
      top = triggerRect.top - popoverRect.height - spacing;
    }
    top = Math.max(spacing, Math.min(top, viewportHeight - popoverRect.height - spacing));

    setPopoverStyle({
      top: `${Math.round(top)}px`,
      left: `${Math.round(left)}px`,
      right: 'auto',
      bottom: 'auto',
      transform: 'none',
    });
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const rafId = window.requestAnimationFrame(updatePopoverPosition);
    const timeoutId = window.setTimeout(updatePopoverPosition, 80);
    const handleReposition = () => updatePopoverPosition();
    const visualViewport = window.visualViewport;

    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    visualViewport?.addEventListener('resize', handleReposition);
    visualViewport?.addEventListener('scroll', handleReposition);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
      visualViewport?.removeEventListener('resize', handleReposition);
      visualViewport?.removeEventListener('scroll', handleReposition);
    };
  }, [calendarViewDate, isOpen, updatePopoverPosition]);

  const todayDateKey = useMemo(() => toDateKey(new Date()), []);

  const calendarDays = useMemo(() => {
    const monthStart = new Date(calendarViewDate.getFullYear(), calendarViewDate.getMonth(), 1);
    const mondayIndex = (monthStart.getDay() + 6) % 7;
    const gridStart = new Date(monthStart);
    gridStart.setDate(monthStart.getDate() - mondayIndex);

    return Array.from({ length: 42 }, (_, index) => {
      const dayDate = new Date(gridStart);
      dayDate.setDate(gridStart.getDate() + index);
      const dayDateKey = toDateKey(dayDate);
      const isDisabled = (minDateKey && dayDateKey < minDateKey) || (maxDateKey && dayDateKey > maxDateKey);
      return {
        date: dayDate,
        dateKey: dayDateKey,
        dayNumber: dayDate.getDate(),
        isCurrentMonth: dayDate.getMonth() === calendarViewDate.getMonth(),
        isSelected: dayDateKey === selectedDateKey,
        isToday: dayDateKey === todayDateKey,
        isDisabled,
      };
    });
  }, [calendarViewDate, maxDateKey, minDateKey, selectedDateKey, todayDateKey]);

  return (
    <Wrapper className={className} ref={wrapperRef}>
      {/* On ajoute une classe CSS native pour permettre à updatePopoverPosition de trouver le trigger */}
      <Trigger className="date-picker-trigger-inner">
        <InlineInput
          id={inputId}
          type="text"
          value={inputValue}
          onChange={handleInlineDateInputChange}
          onBlur={handleInlineDateInputBlur}
          inputMode="numeric"
          autoComplete="off"
          spellCheck={false}
          placeholder={placeholder}
          pattern="[0-9]{2}/[0-9]{2}/[0-9]{4}"
          aria-label={ariaLabel}
          title="Saisir la date (JJ/MM/AAAA)"
          disabled={disabled}
        />
        <OpenButton
          type="button"
          onClick={toggleDatePicker}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          aria-label="Ouvrir le calendrier"
          disabled={disabled}
        >
          <TriggerIcon icon={faCalendarDays} aria-hidden="true" />
        </OpenButton>
      </Trigger>

      {isOpen && typeof document !== 'undefined' && createPortal(
        <>
          <Backdrop
            type="button"
            aria-label="Fermer le calendrier"
            onClick={closeDatePicker}
          />
          <Popover
            ref={popoverRef}
            role="dialog"
            aria-label={dialogLabel}
            style={popoverStyle || undefined}
          >
            <PopoverHeader>
              <MonthButton
                type="button"
                onClick={() => shiftCalendarMonth(-1)}
                aria-label="Mois précédent"
              >
                ‹
              </MonthButton>
              <strong>{formatMonthYear(calendarViewDate)}</strong>
              <MonthButton
                type="button"
                onClick={() => shiftCalendarMonth(1)}
                aria-label="Mois suivant"
              >
                ›
              </MonthButton>
            </PopoverHeader>

            <Weekdays>
              {WEEKDAY_LABELS.map((label) => (
                <span key={label}>{label}</span>
              ))}
            </Weekdays>

            <Grid>
              {calendarDays.map((day) => (
                <DayButton
                  key={day.dateKey}
                  type="button"
                  $isOutside={!day.isCurrentMonth}
                  $isSelected={day.isSelected}
                  $isToday={day.isToday}
                  $isDisabled={day.isDisabled}
                  onClick={() => selectDateFromCalendar(day.date)}
                  aria-label={formatDateShort(day.date)}
                  aria-current={day.isToday ? 'date' : undefined}
                  aria-pressed={day.isSelected}
                  disabled={day.isDisabled}
                >
                  {day.dayNumber}
                </DayButton>
              ))}
            </Grid>

            {showQuickActions && (
              <PopoverFooter>
                <button type="button" onClick={() => goRelativeDay(-1)}>Hier</button>
                <button type="button" onClick={goToday}>Aujourd'hui</button>
                <button type="button" onClick={() => goRelativeDay(1)}>Demain</button>
              </PopoverFooter>
            )}

            <CloseRow>
              <button type="button" onClick={closeDatePicker}>Fermer</button>
            </CloseRow>
          </Popover>
        </>,
        document.body
      )}
    </Wrapper>
  );
};

export default SharedDatePicker;